import React, { useState } from 'react';
import { Crown, Check, Bell, ShieldCheck, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

/**
 * PremiumPaywall Component
 * Shows a blurred overlay with upgrade prompt for free users
 * Matches the design and pricing of the Subscription page
 * @param {boolean} isPremium - Whether user has premium subscription
 * @param {React.ReactNode} children - Content to be blurred for free users
 */
const FeatureItem = ({ text }) => (
  <div className="flex items-start gap-2">
    <span className="text-red-600 mt-0.5"><Check className="w-4 h-4" /></span>
    <p className="text-sm text-gray-700 leading-relaxed">{text}</p>
  </div>
);

const PremiumPaywall = ({ isPremium, children }) => {
  const navigate = useNavigate();

  const [selectedDuration, setSelectedDuration] = useState('1 Month');

  if (isPremium) {
    return <>{children}</>;
  }

  const planOptions = [
    { label: '1 Month', amount: 199 },
    { label: '3 Months', amount: 579 },
    { label: '6 Months', amount: 1179 },
    { label: '12 Months', amount: 2299 },
  ];

  return (
    <div className="relative">
      {/* Blurred Page Content Only - Not Sidebar */}
      <div className="relative blur-md">
        {children}
      </div>

      {/* Fixed Modal Overlay - Non-Interactive background */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto pointer-events-none">
        {/* Modal Card - Only this is interactive */}
        <div className="max-w-5xl w-full bg-white rounded-3xl shadow-2xl border border-gray-200 overflow-hidden my-8 pointer-events-auto">
          {/* Hero Header */}
          <div className="bg-gradient-to-r from-red-900 via-red-800 to-red-700 px-6 sm:px-10 pt-8 pb-6 text-center border-b border-red-200/60">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 border border-red-300/40 rounded-full text-xs font-semibold text-white mb-4">
              <span className="text-white">●</span> Subscription Plans
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">Choose Your Plan</h2>
            <p className="text-white/90 mt-2 text-sm sm:text-base">Select the perfect plan to unlock your career potential</p>

            <div className="flex flex-wrap items-center justify-center gap-2 mt-4 text-xs font-semibold">
              <span className="px-3 py-1 bg-white/15 text-white rounded-full border border-white/20">PLAN: Free</span>
              <span className="px-3 py-1 bg-white/15 text-white rounded-full border border-white/20">STATUS: Active</span>
              <span className="px-3 py-1 bg-white/15 text-white rounded-full border border-white/20">Expires: Lifetime</span>
            </div>
          </div>

          {/* Plans */}
          <div className="px-4 sm:px-8 py-8 bg-white">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Basic */}
              <div className="border border-gray-200 rounded-2xl bg-white shadow-sm p-6 flex flex-col">
                <div className="flex items-center justify-between mb-4">
                  <span className="px-3 py-1 bg-gray-100 text-gray-700 text-xs font-semibold rounded-full">Free Forever</span>
                </div>
                <h3 className="text-2xl font-bold text-gray-900">Basic</h3>
                <p className="text-lg font-bold text-gray-900 mt-1">₹0<span className="text-sm font-semibold">/month</span></p>
                <div className="mt-4 space-y-3 text-sm text-gray-700">
                  <FeatureItem text="Access to VetriaAI dashboard and skill badges" />
                  <FeatureItem text="Limited assessments and job applications each month" />
                  <FeatureItem text="Standard support through support tickets" />
                </div>
                <button
                  onClick={() => navigate('/job-seeker/subscription')}
                  className="mt-auto w-full rounded-xl border-2 border-gray-200 text-gray-700 font-semibold py-3 bg-gray-50"
                >
                  Current Plan
                </button>
              </div>

              {/* Pro */}
              <div className="relative border-2 border-red-300 rounded-2xl bg-white shadow-md p-6 flex flex-col">
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-red-700 text-white text-xs font-semibold rounded-full shadow">Most Popular</div>
                <h3 className="text-2xl font-bold text-gray-900 mt-2">Pro</h3>
                <div className="flex items-baseline gap-2 mt-2">
                  <span className="text-gray-400 line-through text-lg">₹299</span>
                  <span className="text-3xl font-extrabold text-red-700">₹199</span>
                </div>
                <p className="text-sm text-gray-600">Billed monthly</p>

                {/* Duration options */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-4">
                  {planOptions.map((opt) => {
                    const active = selectedDuration === opt.label;
                    return (
                      <button
                        key={opt.label}
                        onClick={() => setSelectedDuration(opt.label)}
                        className={`rounded-lg border px-3 py-2 text-sm font-semibold transition ${active ? 'border-red-700 bg-red-50 text-red-800' : 'border-gray-200 text-gray-800 hover:border-red-500 hover:bg-red-50'}`}
                      >
                        {opt.label}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-4 space-y-3 text-sm text-gray-700">
                  <FeatureItem text="Everything in Basic" />
                  <FeatureItem text="Unlimited assessments and job recommendations" />
                  <FeatureItem text="Personalized courses and learning paths" />
                  <FeatureItem text="Advanced analytics on skills and progress" />
                  <FeatureItem text="Priority support" />
                </div>

                <button
                  onClick={() => navigate('/job-seeker/subscription')}
                  className="mt-6 w-full rounded-xl bg-gradient-to-r from-red-700 to-red-900 text-white font-semibold py-3 shadow-lg hover:from-red-800 hover:to-red-950 transition"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Crown className="w-5 h-5" />
                    <span>Subscribe Now</span>
                  </div>
                </button>

                <div className="mt-4 text-xs text-gray-600 space-y-1">
                  <p>Plan: Premium - {selectedDuration}</p>
                  <p>Amount: ₹{planOptions.find(p => p.label === selectedDuration)?.amount}</p>
                  <p>Duration: 1 month</p>
                  <div className="flex items-center gap-2 text-gray-500 mt-2 text-[11px]">
                    <ShieldCheck className="w-4 h-4" />
                    <span>Secure payment powered by Razorpay</span>
                  </div>
                </div>
              </div>

              {/* Premium coming soon */}
              <div className="border border-gray-200 rounded-2xl bg-white shadow-sm p-6 flex flex-col">
                <div className="flex items-center gap-2 mb-2">
                  <h3 className="text-2xl font-bold text-gray-900">Premium</h3>
                  <span className="px-3 py-1 bg-red-100 text-red-700 text-xs font-semibold rounded-full">Coming Soon</span>
                </div>
                <p className="text-sm text-gray-600 mb-4">A plan for power users, launching soon.</p>
                <label className="text-xs font-semibold text-gray-700">Get notified when available</label>
                <div className="mt-2 flex gap-2">
                  <input
                    type="email"
                    placeholder="Enter your email"
                    className="flex-1 rounded-xl border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-200"
                  />
                  <button className="w-11 h-11 rounded-xl bg-red-600 text-white flex items-center justify-center shadow hover:bg-red-700">
                    <Bell className="w-5 h-5" />
                  </button>
                </div>
                <p className="text-[11px] text-gray-500 mt-3">We'll notify you when Premium launches</p>

                <div className="mt-auto pt-4 text-xs text-gray-600 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gray-500" />
                  <span>Priority roadmap notifications</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PremiumPaywall;
